export const MANUSCRIPT_CONTENT_VERSION =
  1;

export const MANUSCRIPT_CONTENT_LIMITS =
  Object.freeze({
    maximumBlocks: 5000,
    maximumRuns: 20000,
    maximumRunsPerBlock: 1000,
    maximumTextCharacters: 160000,
    maximumBlockIdCharacters: 120,
    maximumNumberingIdCharacters: 120,
    maximumIndentation: 14400,
    maximumListLevel: 8,
  });

export const MANUSCRIPT_BLOCK_TYPES =
  Object.freeze([
    "paragraph",
    "heading",
    "list_item",
    "blockquote",
    "page_break",
  ]);

export const MANUSCRIPT_ALIGNMENT_VALUES =
  Object.freeze([
    "left",
    "center",
    "right",
    "justify",
  ]);

export const MANUSCRIPT_LINE_SPACING_VALUES =
  Object.freeze([
    1,
    1.15,
    1.5,
    2,
  ]);

export const MANUSCRIPT_LIST_TYPES =
  Object.freeze([
    "bullet",
    "ordered",
  ]);

export const MANUSCRIPT_FONT_FAMILIES =
  Object.freeze([
    "Georgia",
    "Times New Roman",
    "Arial",
    "Verdana",
    "Courier New",
  ]);

export const MANUSCRIPT_FONT_SIZE_LIMITS =
  Object.freeze({
    minimum: 8,
    maximum: 72,
  });

const BLOCK_TYPE_SET =
  new Set(
    MANUSCRIPT_BLOCK_TYPES
  );

const ALIGNMENT_SET =
  new Set(
    MANUSCRIPT_ALIGNMENT_VALUES
  );

const LINE_SPACING_SET =
  new Set(
    MANUSCRIPT_LINE_SPACING_VALUES
  );

const LIST_TYPE_SET =
  new Set(
    MANUSCRIPT_LIST_TYPES
  );

const FONT_FAMILY_SET =
  new Set(
    MANUSCRIPT_FONT_FAMILIES
  );

function safeObject(value) {
  return value &&
    typeof value === "object" &&
    !Array.isArray(value)
    ? value
    : {};
}

function cleanBoundedString(
  value,
  maximumLength
) {
  if (typeof value !== "string") {
    return "";
  }

  return value
    .trim()
    .slice(
      0,
      maximumLength
    );
}

function hasOwn(
  value,
  key
) {
  return Object.prototype
    .hasOwnProperty.call(
      value,
      key
    );
}

function normalizeBoundedInteger(
  value,
  context
) {
  const {
    errors,
    label,
    minimum,
    maximum,
    fallback = 0,
    provided = false,
  } = context;

  if (!provided) {
    return fallback;
  }

  const number =
    Number(value);

  if (
    !Number.isFinite(number) ||
    !Number.isInteger(number) ||
    number < minimum ||
    number > maximum
  ) {
    errors.push(
      `${label} must be an integer between ${minimum} and ${maximum}.`
    );

    return fallback;
  }

  return number;
}

function uniqueBlockId(
  requestedId,
  blockIndex,
  usedIds
) {
  const cleaned =
    cleanBoundedString(
      requestedId,
      MANUSCRIPT_CONTENT_LIMITS
        .maximumBlockIdCharacters
    );

  const fallbackBase =
    `block-${blockIndex + 1}`;

  let candidate =
    cleaned ||
    fallbackBase;

  if (
    usedIds.has(candidate)
  ) {
    candidate =
      fallbackBase;
  }

  let suffix = 2;

  while (
    usedIds.has(candidate)
  ) {
    candidate =
      `${fallbackBase}-${suffix}`;

    suffix += 1;
  }

  usedIds.add(candidate);

  return candidate;
}

function normalizeIndentation(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    provided,
  } = context;

  if (
    provided &&
    (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value)
    )
  ) {
    errors.push(
      `Block ${blockIndex + 1} indentation must be an object.`
    );
  }

  const source =
    safeObject(value);

  const maximum =
    MANUSCRIPT_CONTENT_LIMITS
      .maximumIndentation;

  return {
    left:
      normalizeBoundedInteger(
        source.left,
        {
          errors,
          label:
            `Block ${blockIndex + 1} left indentation`,
          minimum: 0,
          maximum,
          provided:
            hasOwn(
              source,
              "left"
            ),
        }
      ),

    right:
      normalizeBoundedInteger(
        source.right,
        {
          errors,
          label:
            `Block ${blockIndex + 1} right indentation`,
          minimum: 0,
          maximum,
          provided:
            hasOwn(
              source,
              "right"
            ),
        }
      ),

    firstLine:
      normalizeBoundedInteger(
        source.firstLine,
        {
          errors,
          label:
            `Block ${blockIndex + 1} first-line indentation`,
          minimum: 0,
          maximum,
          provided:
            hasOwn(
              source,
              "firstLine"
            ),
        }
      ),

    hanging:
      normalizeBoundedInteger(
        source.hanging,
        {
          errors,
          label:
            `Block ${blockIndex + 1} hanging indentation`,
          minimum: 0,
          maximum,
          provided:
            hasOwn(
              source,
              "hanging"
            ),
        }
      ),
  };
}

function normalizeList(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    provided,
  } = context;

  if (
    provided &&
    (
      !value ||
      typeof value !== "object" ||
      Array.isArray(value)
    )
  ) {
    errors.push(
      `Block ${blockIndex + 1} list metadata must be an object.`
    );
  }

  const source =
    safeObject(value);

  let type =
    "bullet";

  if (
    hasOwn(
      source,
      "type"
    )
  ) {
    if (
      LIST_TYPE_SET.has(
        source.type
      )
    ) {
      type =
        source.type;
    } else {
      errors.push(
        `Block ${blockIndex + 1} has an unsupported list type.`
      );
    }
  }

  let numberingId =
    "";

  if (
    hasOwn(
      source,
      "numberingId"
    )
  ) {
    if (
      typeof source.numberingId !==
      "string"
    ) {
      errors.push(
        `Block ${blockIndex + 1} numbering ID must be a string.`
      );
    } else {
      if (
        source.numberingId.length >
        MANUSCRIPT_CONTENT_LIMITS
          .maximumNumberingIdCharacters
      ) {
        errors.push(
          `Block ${blockIndex + 1} numbering ID exceeds the character limit.`
        );
      }

      numberingId =
        cleanBoundedString(
          source.numberingId,
          MANUSCRIPT_CONTENT_LIMITS
            .maximumNumberingIdCharacters
        );
    }
  }

  return {
    type,

    level:
      normalizeBoundedInteger(
        source.level,
        {
          errors,
          label:
            `Block ${blockIndex + 1} list level`,
          minimum: 0,
          maximum:
            MANUSCRIPT_CONTENT_LIMITS
              .maximumListLevel,
          provided:
            hasOwn(
              source,
              "level"
            ),
        }
      ),

    numberingId,
  };
}

function normalizeBooleanMark(
  value,
  key,
  context
) {
  const {
    errors,
    blockIndex,
    runIndex,
  } = context;

  if (
    !hasOwn(
      value,
      key
    )
  ) {
    return false;
  }

  if (
    typeof value[key] !==
    "boolean"
  ) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} ${key} must be a boolean.`
    );

    return false;
  }

  return value[key];
}

function normalizeOptionalFontFamily(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    runIndex,
    provided,
  } = context;

  if (!provided) {
    return undefined;
  }

  if (typeof value !== "string") {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} font family must be a string.`
    );
    return undefined;
  }

  const cleaned = value.trim();

  if (!cleaned) {
    return undefined;
  }

  if (!FONT_FAMILY_SET.has(cleaned)) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} has an unsupported font family.`
    );
    return undefined;
  }

  return cleaned;
}

function normalizeOptionalFontSize(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    runIndex,
    provided,
  } = context;

  if (!provided || value === "" || value === null) {
    return undefined;
  }

  const number = Number(value);
  const { minimum, maximum } = MANUSCRIPT_FONT_SIZE_LIMITS;

  if (
    !Number.isFinite(number) ||
    !Number.isInteger(number) ||
    number < minimum ||
    number > maximum
  ) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} font size must be an integer between ${minimum} and ${maximum}.`
    );
    return undefined;
  }

  return number;
}

function normalizeRun(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    runIndex,
    state,
  } = context;

  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} must be an object.`
    );

    return null;
  }

  if (
    typeof value.text !==
    "string"
  ) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} must contain text.`
    );

    return null;
  }

  if (
    value.text.includes(
      "\f"
    )
  ) {
    errors.push(
      `Block ${blockIndex + 1}, run ${runIndex + 1} must use an explicit page-break block instead of a form-feed character.`
    );

    return null;
  }

  if (
    state.runCount >=
    MANUSCRIPT_CONTENT_LIMITS
      .maximumRuns
  ) {
    if (
      !state.runLimitReported
    ) {
      errors.push(
        "Manuscript content exceeds the total run limit."
      );

      state.runLimitReported =
        true;
    }

    return null;
  }

  if (
    state.textCharacterCount +
      value.text.length >
    MANUSCRIPT_CONTENT_LIMITS
      .maximumTextCharacters
  ) {
    if (
      !state.textLimitReported
    ) {
      errors.push(
        "Manuscript content exceeds the text-character limit."
      );

      state.textLimitReported =
        true;
    }

    return null;
  }

  state.runCount += 1;
  state.textCharacterCount +=
    value.text.length;

  const run = {
    text:
      value.text,

    bold:
      normalizeBooleanMark(
        value,
        "bold",
        {
          errors,
          blockIndex,
          runIndex,
        }
      ),

    italic:
      normalizeBooleanMark(
        value,
        "italic",
        {
          errors,
          blockIndex,
          runIndex,
        }
      ),

    underline:
      normalizeBooleanMark(
        value,
        "underline",
        {
          errors,
          blockIndex,
          runIndex,
        }
      ),

    highlight:
      normalizeBooleanMark(
        value,
        "highlight",
        {
          errors,
          blockIndex,
          runIndex,
        }
      ),
  };

  const fontFamily =
    normalizeOptionalFontFamily(
      value.fontFamily,
      {
        errors,
        blockIndex,
        runIndex,
        provided: hasOwn(value, "fontFamily"),
      }
    );

  const fontSize =
    normalizeOptionalFontSize(
      value.fontSize,
      {
        errors,
        blockIndex,
        runIndex,
        provided: hasOwn(value, "fontSize"),
      }
    );

  if (fontFamily !== undefined) {
    run.fontFamily = fontFamily;
  }

  if (fontSize !== undefined) {
    run.fontSize = fontSize;
  }

  return run;
}

function normalizeBlock(
  value,
  context
) {
  const {
    errors,
    blockIndex,
    usedIds,
    state,
  } = context;

  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    errors.push(
      `Block ${blockIndex + 1} must be an object.`
    );

    return null;
  }

  if (
    !BLOCK_TYPE_SET.has(
      value.type
    )
  ) {
    errors.push(
      `Block ${blockIndex + 1} has an unsupported type.`
    );

    return null;
  }

  const block = {
    id:
      uniqueBlockId(
        value.id,
        blockIndex,
        usedIds
      ),

    type:
      value.type,
  };

  const runsProvided =
    hasOwn(
      value,
      "runs"
    );

  if (
    value.type ===
    "page_break"
  ) {
    if (
      runsProvided &&
      !Array.isArray(
        value.runs
      )
    ) {
      errors.push(
        `Block ${blockIndex + 1} page-break runs must be an array when provided.`
      );
    } else if (
      Array.isArray(
        value.runs
      ) &&
      value.runs.length
    ) {
      errors.push(
        `Block ${blockIndex + 1} is a page break and cannot contain text runs.`
      );
    }

    return block;
  }

  if (
    runsProvided &&
    !Array.isArray(
      value.runs
    )
  ) {
    errors.push(
      `Block ${blockIndex + 1} runs must be an array.`
    );
  }

  const sourceRuns =
    Array.isArray(
      value.runs
    )
      ? value.runs
      : [];

  if (
    sourceRuns.length >
    MANUSCRIPT_CONTENT_LIMITS
      .maximumRunsPerBlock
  ) {
    errors.push(
      `Block ${blockIndex + 1} exceeds the run limit.`
    );
  }

  const perBlockRunCount =
    Math.min(
      sourceRuns.length,
      MANUSCRIPT_CONTENT_LIMITS
        .maximumRunsPerBlock
    );

  const availableRunCount =
    Math.max(
      0,
      MANUSCRIPT_CONTENT_LIMITS
        .maximumRuns -
        state.runCount
    );

  if (
    perBlockRunCount >
      availableRunCount &&
    !state.runLimitReported
  ) {
    errors.push(
      "Manuscript content exceeds the total run limit."
    );

    state.runLimitReported =
      true;
  }

  block.runs =
    sourceRuns
      .slice(
        0,
        Math.min(
          perBlockRunCount,
          availableRunCount
        )
      )
      .map(
        (
          run,
          runIndex
        ) =>
          normalizeRun(
            run,
            {
              errors,
              blockIndex,
              runIndex,
              state,
            }
          )
      )
      .filter(Boolean);

  if (
    !hasOwn(
      value,
      "alignment"
    ) ||
    value.alignment ===
      undefined ||
    value.alignment ===
      null ||
    value.alignment ===
      ""
  ) {
    block.alignment =
      "left";
  } else if (
    ALIGNMENT_SET.has(
      value.alignment
    )
  ) {
    block.alignment =
      value.alignment;
  } else {
    errors.push(
      `Block ${blockIndex + 1} has an unsupported alignment.`
    );

    block.alignment =
      "left";
  }

  if (
    hasOwn(
      value,
      "lineSpacing"
    ) &&
    value.lineSpacing !==
      undefined &&
    value.lineSpacing !==
      null &&
    value.lineSpacing !==
      ""
  ) {
    const lineSpacing =
      Number(
        value.lineSpacing
      );

    if (
      LINE_SPACING_SET.has(
        lineSpacing
      )
    ) {
      block.lineSpacing =
        lineSpacing;
    } else {
      errors.push(
        `Block ${blockIndex + 1} has unsupported line spacing.`
      );
    }
  }

  block.indentation =
    normalizeIndentation(
      value.indentation,
      {
        errors,
        blockIndex,
        provided:
          hasOwn(
            value,
            "indentation"
          ),
      }
    );

  if (
    value.type ===
    "heading"
  ) {
    block.level =
      normalizeBoundedInteger(
        value.level,
        {
          errors,
          label:
            `Block ${blockIndex + 1} heading level`,
          minimum: 1,
          maximum: 6,
          fallback: 1,
          provided:
            hasOwn(
              value,
              "level"
            ),
        }
      );
  }

  if (
    value.type ===
    "list_item"
  ) {
    block.list =
      normalizeList(
        value.list,
        {
          errors,
          blockIndex,
          provided:
            hasOwn(
              value,
              "list"
            ),
        }
      );
  }

  return block;
}

function contentStats(
  content
) {
  const blocks =
    Array.isArray(
      content?.blocks
    )
      ? content.blocks
      : [];

  let runCount = 0;
  let textCharacterCount = 0;

  blocks.forEach(
    (block) => {
      const runs =
        Array.isArray(
          block?.runs
        )
          ? block.runs
          : [];

      runCount +=
        runs.length;

      runs.forEach(
        (run) => {
          textCharacterCount +=
            typeof run?.text ===
            "string"
              ? run.text.length
              : 0;
        }
      );
    }
  );

  return {
    blockCount:
      blocks.length,

    runCount,

    textCharacterCount,
  };
}

function projectNormalizedContent(
  content
) {
  return content.blocks.reduce(
    (
      text,
      block,
      blockIndex
    ) => {
      if (
        block.type ===
        "page_break"
      ) {
        return `${text}\f`;
      }

      const blockText =
        block.runs
          .map(
            (run) =>
              run.text
          )
          .join("");

      const previousBlock =
        content.blocks[
          blockIndex - 1
        ];

      if (
        blockIndex > 0 &&
        previousBlock?.type !==
          "page_break"
      ) {
        return `${text}\n\n${blockText}`;
      }

      return `${text}${blockText}`;
    },
    ""
  );
}

export function manuscriptContentFromPlainText(
  value
) {
  const text =
    typeof value === "string"
      ? value
      : "";

  if (!text) {
    return {
      version:
        MANUSCRIPT_CONTENT_VERSION,

      blocks: [],
    };
  }

  const blocks = [];
  const pageSections =
    text.split("\f");

  pageSections.forEach(
    (
      section,
      sectionIndex
    ) => {
      if (section) {
        blocks.push({
          id:
            `block-${blocks.length + 1}`,

          type:
            "paragraph",

          alignment:
            "left",

          indentation: {
            left: 0,
            right: 0,
            firstLine: 0,
            hanging: 0,
          },

          runs: [
            {
              text:
                section,

              bold: false,
              italic: false,
              underline: false,
            },
          ],
        });
      }

      if (
        sectionIndex <
        pageSections.length - 1
      ) {
        blocks.push({
          id:
            `block-${blocks.length + 1}`,

          type:
            "page_break",
        });
      }
    }
  );

  return {
    version:
      MANUSCRIPT_CONTENT_VERSION,

    blocks,
  };
}

export function normalizeManuscriptContent(
  value,
  options = {}
) {
  const errors = [];
  const warnings = [];
  const settings =
    safeObject(
      options
    );

  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    if (
      typeof settings.fallbackText ===
      "string"
    ) {
      const fallbackResult =
        normalizeManuscriptContent(
          manuscriptContentFromPlainText(
            settings.fallbackText
          )
        );

      return {
        ...fallbackResult,

        warnings: [
          ...fallbackResult.warnings,
          "Structured manuscript content was unavailable, so plain text was preserved.",
        ],
      };
    }

    return {
      ok: false,
      content: null,
      errors: [
        "Manuscript content must be an object.",
      ],
      warnings,
      stats: {
        blockCount: 0,
        runCount: 0,
        textCharacterCount: 0,
      },
    };
  }

  if (
    value.version !==
    MANUSCRIPT_CONTENT_VERSION
  ) {
    errors.push(
      `Unsupported manuscript content version: ${String(value.version)}.`
    );
  }

  if (
    !Array.isArray(
      value.blocks
    )
  ) {
    errors.push(
      "Manuscript content blocks must be an array."
    );
  }

  const sourceBlocks =
    Array.isArray(
      value.blocks
    )
      ? value.blocks
      : [];

  if (
    sourceBlocks.length >
    MANUSCRIPT_CONTENT_LIMITS
      .maximumBlocks
  ) {
    errors.push(
      "Manuscript content exceeds the block limit."
    );
  }

  const usedIds =
    new Set();

  const state = {
    runCount: 0,
    textCharacterCount: 0,
    runLimitReported: false,
    textLimitReported: false,
  };

  const blocks =
    sourceBlocks
      .slice(
        0,
        MANUSCRIPT_CONTENT_LIMITS
          .maximumBlocks
      )
      .map(
        (
          block,
          blockIndex
        ) =>
          normalizeBlock(
            block,
            {
              errors,
              blockIndex,
              usedIds,
              state,
            }
          )
      )
      .filter(Boolean);

  const candidateContent = {
    version:
      MANUSCRIPT_CONTENT_VERSION,

    blocks,
  };

  const stats =
    contentStats(
      candidateContent
    );

  const ok =
    errors.length === 0;

  return {
    ok,

    content:
      ok
        ? candidateContent
        : null,

    errors,
    warnings,
    stats,
  };
}


export function hasManuscriptContentContract(
  value
) {
  const result =
    normalizeManuscriptContent(
      value
    );

  return Boolean(
    result.ok &&
    result.content
  );
}


export function hasStructuredManuscriptContent(
  value
) {
  const result =
    normalizeManuscriptContent(
      value
    );

  return (
    result.ok &&
    result.content.blocks.length >
      0
  );
}

export function plainTextFromManuscriptContent(
  value,
  options = {}
) {
  const settings =
    safeObject(
      options
    );

  const result =
    normalizeManuscriptContent(
      value
    );

  if (
    result.ok &&
    result.content
  ) {
    return projectNormalizedContent(
      result.content
    );
  }

  return typeof settings.fallbackText ===
    "string"
    ? settings.fallbackText
    : "";
}

export function plainTextFromManuscriptEntry(
  value
) {
  const source =
    safeObject(
      value
    );

  if (
    hasOwn(
      source,
      "manuscriptContent"
    )
  ) {
    return plainTextFromManuscriptContent(
      source.manuscriptContent
    );
  }

  return typeof source.manuscriptText ===
    "string"
    ? source.manuscriptText
    : "";
}


export function countWordsFromManuscriptEntry(
  value
) {
  const text =
    plainTextFromManuscriptEntry(
      value
    )
      .replace(
        /\f/g,
        " "
      )
      .trim();

  return text
    ? text
        .split(
          /\s+/
        )
        .filter(Boolean)
        .length
    : 0;
}


export function countWordsFromManuscriptContent(
  value,
  options = {}
) {
  const text =
    plainTextFromManuscriptContent(
      value,
      options
    )
      .replace(
        /\f/g,
        " "
      )
      .trim();

  if (!text) {
    return 0;
  }

  return text
    .split(/\s+/)
    .filter(Boolean)
    .length;
}
